import { create } from "zustand";
import {
  ProgrammingFilters,
  ProgrammingRow,
  ProgrammingSummary,
  SlotBlock,
  SlotInstructor,
  SlotClassroom,
} from "@services/types/programming-edit/programmingEditTypes";
import {
  AdaptedStage,
  autoAssignStagesToSlots,
  adaptPartsToStages,
  adaptStructureToSelectOption,
} from "@services/collections/programming-edit/programmingEditAdapters";
import { MicroprogrammingPreviewSection, ProgrammingStructure } from "@models/ISchema";
import { ProgrammingSessionCrudInput } from "@services/types/programming-edit/programmingEditTypes";
import { blocksToHours, normalizeHHMM } from "@utils/blocks";

export interface StructureSelectOption {
  value: string;
  label: string;
}

/**
 * Global state for the "Edit Programming" view.
 * Uses Zustand (instead of Context) so the "Edit Assignation" tab
 * can read `hasChanges` without being inside the same provider tree.
 */
interface ProgrammingEditState {
  /** ID of the programming structure selected in the main Select dropdown */
  selectedStructureId: string | null;

  /** The currently selected structure option — persists across tab switches */
  selectedStructureOption: StructureSelectOption | null;

  /**
   * Editable rows of the micro-programming table.
   * Each row is an individual date/slot extracted from a session (atomic level).
   * Sessions group multiple dates; micro-programming breaks them into
   * individual slots to assign stages, instructors, and classrooms per date.
   */
  slots: ProgrammingRow[];

  /**
   * Immutable snapshot of slots at the time of loading from the API.
   * Used to compare against `slots` to derive `hasChanges`.
   * Only updated on initial fetch or after a successful "Validate and apply".
   */
  originalSlots: ProgrammingRow[];

  /** Programming progress summary: completed stages, pending blocks, etc. */
  summary: ProgrammingSummary | null;

  /**
   * Catalog of available stages for the inline Select in each table row.
   * Comes from the selected programming structure.
   */
  stages: AdaptedStage[];

  /** Active filters on the table (client-side). Do not affect the query. */
  filters: ProgrammingFilters;

  /** Whether data is being loaded from the API */
  isLoading: boolean;

  /** Whether the "Validate and apply" mutation is executing */
  isValidating: boolean;

  /**
   * Main cross-tab change detection flag.
   * Computed by comparing `slots` vs `originalSlots` on editable fields
   * (stageId, instructors, classrooms, splitPart).
   * The "Edit Assignation" tab reads this to show warnings.
   */
  hasChanges: boolean;

  /** Preview sections returned by microprogrammingPreview, used to display in ResultEdit */
  previewSections: MicroprogrammingPreviewSection[] | null;

  /** Changeset built from preview sections, stored for the dryRun and final commit */
  programmingChangeset: ProgrammingSessionCrudInput | null;

  /** True when the programming validation results are ready and ResultEdit should be shown */
  isProgrammingResultsActive: boolean;
}

const initialFilters: ProgrammingFilters = {
  dateFrom: null,
  dateTo: null,
  dayOfWeek: null,
  component: null,
};

const initialState: ProgrammingEditState = {
  selectedStructureId: null,
  selectedStructureOption: null,
  slots: [],
  originalSlots: [],
  summary: null,
  stages: [],
  filters: initialFilters,
  isLoading: false,
  isValidating: false,
  hasChanges: false,
  previewSections: null,
  programmingChangeset: null,
  isProgrammingResultsActive: false,
};

export const useProgrammingEditStore = create<ProgrammingEditState>(() =>
  structuredClone(initialState),
);

/** Resets the entire store to its initial state. Called on "Reiniciar" or on unmount. */
export const resetProgrammingEditStore = () =>
  useProgrammingEditStore.setState(structuredClone(initialState));

/** Sets the selected programming structure (ID + label). The consuming hook should re-fetch slots on change. */
export const setProgrammingStructure = (option: StructureSelectOption | null) =>
  useProgrammingEditStore.setState({
    selectedStructureId: option?.value ?? null,
    selectedStructureOption: option,
  });

/**
 * Derives the programming summary from current slots and stages.
 * Called after any action that modifies slot stage assignments.
 */
const computeSummaryFromSlots = () => {
  const { slots, stages } = useProgrammingEditStore.getState();
  if (!stages.length) {
    useProgrammingEditStore.setState({ summary: null });
    return;
  }

  const stageNameByCode = new Map(stages.map(s => [s.code ?? "", s.name ?? ""]));

  const stageProgresses = stages.map(stage => {
    const assignedSlots = slots.filter(s => s.stageId === stage.id);
    const assignedBlocks = assignedSlots.reduce((sum, s) => sum + s.durationBlocks, 0);
    const assignedHours = assignedSlots.reduce((sum, s) => sum + s.durationHours, 0);
    const totalBlocks = stage.blockDuration;
    const totalHours = blocksToHours(totalBlocks);
    const percentage = totalBlocks > 0 ? Math.round((assignedBlocks / totalBlocks) * 100) : 0;

    const dependencyLabel =
      stage?.dependencyCodes?.length > 0
        ? stage?.dependencyCodes?.map(code => stageNameByCode.get(code) ?? code).join(", ")
        : null;

    return {
      id: stage.id,
      name: stage.name ?? "",
      assignedBlocks,
      totalBlocks,
      assignedHours,
      totalHours,
      percentage,
      isComplete: assignedBlocks === totalBlocks && totalBlocks > 0,
      dependencyLabel,
    };
  });

  const slotsWithoutStage = slots.filter(s => !s.stageId);

  const summary: ProgrammingSummary = {
    completedStages: stageProgresses.filter(s => s.isComplete).length,
    totalStages: stageProgresses.length,
    classesToAssign: slotsWithoutStage.length,
    pendingBlocks: slotsWithoutStage.reduce((sum, s) => sum + s.durationBlocks, 0),
    incompleteCount: stageProgresses.filter(s => s.percentage > 0 && !s.isComplete).length,
    stages: stageProgresses,
  };

  useProgrammingEditStore.setState({ summary });
};

/**
 * Stores slots AND takes a snapshot (originalSlots).
 * Applies auto-stage-assignment before snapshotting so the initial match
 * does not register as a user change (`hasChanges` stays false).
 */
export const setProgrammingRows = (slots: ProgrammingRow[]) => {
  const { stages } = useProgrammingEditStore.getState();
  const assigned = autoAssignStagesToSlots(slots, stages);
  useProgrammingEditStore.setState({
    slots: structuredClone(assigned),
    originalSlots: structuredClone(assigned),
    hasChanges: false,
  });
  computeSummaryFromSlots();
};

/** Sets the stage catalog used by the inline Select in each table row. */
export const setProgrammingStages = (stages: AdaptedStage[]) =>
  useProgrammingEditStore.setState({ stages });

/**
 * Single source of truth for "apply a structure to the store": sets the selected
 * option and derives its stage catalog. Used both by manual selection in the
 * action bar and by automatic pre-selection on entering the view, so the rule
 * for how a structure is applied lives in one place. Passing `null` clears it.
 */
export const applyStructureSelection = (structure: ProgrammingStructure | null) => {
  setProgrammingStructure(structure ? adaptStructureToSelectOption(structure) : null);
  const parts = structure?.programmingStructureParts?.filter(Boolean) ?? [];
  setProgrammingStages(adaptPartsToStages(parts));
};

/**
 * Compares current slots against the original snapshot on editable fields.
 * Returns true if any slot differs in stageId, instructors, classroom, or splitPart.
 */
const computeHasChanges = (current: ProgrammingRow[], original: ProgrammingRow[]): boolean => {
  if (current.length !== original.length) return true;
  return current.some(slot => {
    const orig = original.find(o => o.id === slot.id);
    if (!orig) return true;
    if (slot.stageId !== orig.stageId) return true;
    if (slot.splitPart !== orig.splitPart) return true;
    const slotClassroomIds = slot.classrooms
      .map(c => c.id)
      .sort()
      .join(",");
    const origClassroomIds = orig.classrooms
      .map(c => c.id)
      .sort()
      .join(",");
    if (slotClassroomIds !== origClassroomIds) return true;
    if (slot.instructors.length !== orig.instructors.length) return true;
    if (slot.instructors.some((inst, i) => inst.id !== orig.instructors[i]?.id)) return true;
    return false;
  });
};

/**
 * Inline edit of the "Stage" column.
 * Recalculates hasChanges after updating.
 */
export const updateSlotStage = (slotId: string, stageId: string | null) => {
  const { slots, originalSlots } = useProgrammingEditStore.getState();
  const updated = slots.map(s => (s.id === slotId ? { ...s, stageId } : s));
  useProgrammingEditStore.setState({
    slots: updated,
    hasChanges: computeHasChanges(updated, originalSlots),
  });
  computeSummaryFromSlots();
};

/**
 * Assigns instructors to a slot from the Drawer.
 * Recalculates hasChanges after updating.
 */
export const updateSlotInstructors = (slotId: string, instructors: SlotInstructor[]) => {
  const { slots, originalSlots } = useProgrammingEditStore.getState();
  const updated = slots.map(s => (s.id === slotId ? { ...s, instructors } : s));
  useProgrammingEditStore.setState({
    slots: updated,
    hasChanges: computeHasChanges(updated, originalSlots),
  });
};

/**
 * Assigns classrooms to a slot from the Drawer.
 * Recalculates hasChanges after updating.
 */
export const updateSlotClassrooms = (slotId: string, classrooms: SlotClassroom[]) => {
  const { slots, originalSlots } = useProgrammingEditStore.getState();
  const updated = slots.map(s => (s.id === slotId ? { ...s, classrooms } : s));
  useProgrammingEditStore.setState({
    slots: updated,
    hasChanges: computeHasChanges(updated, originalSlots),
  });
};

interface SplitConfig {
  parts: number;
  blocksPerPart: number[];
}

const MINUTES_PER_BLOCK = 30;

const computePartTimes = (
  baseStartTime: string,
  blocksPerPart: number[],
): { startTime: string; endTime: string }[] => {
  const [h, m] = baseStartTime.split(":").map(Number);
  let cursor = h * 60 + m;

  return blocksPerPart.map(blocks => {
    const start = cursor;
    cursor += blocks * MINUTES_PER_BLOCK;
    const format = (mins: number) => {
      const hh = String(Math.floor(mins / 60)).padStart(2, "0");
      const mm = String(mins % 60).padStart(2, "0");
      return `${hh}:${mm}`;
    };
    return { startTime: format(start), endTime: format(cursor) };
  });
};

const filterBlocksInRange = (
  blocks: SlotBlock[],
  partStart: string,
  partEnd: string,
): SlotBlock[] => {
  const start = normalizeHHMM(partStart);
  const end = normalizeHHMM(partEnd);
  if (!start || !end) return [];
  return blocks.filter(b => {
    const bStart = normalizeHHMM(b?.startingTime);
    const bEnd = normalizeHHMM(b?.endingTime);
    return !!bStart && !!bEnd && bStart >= start && bEnd <= end;
  });
};

/**
 * Splits a slot into N parts in the UI based on the provided configuration.
 * Generates temporary IDs and a shared splitGroupId.
 * The actual split is persisted when "Validate and apply" is submitted.
 *
 * Each part's `blocks` are filtered from the original slot's `blocks`, which
 * preserves day-specific block IDs (the slot's blocks come from `session.blocks`).
 * Instructors and classrooms are inherited by every part. Subsequent parts keep
 * requiring an explicit stage assignment because a split may distribute blocks
 * across different programming stages.
 */
export const splitSlot = (slotId: string, config: SplitConfig) => {
  const { slots, originalSlots } = useProgrammingEditStore.getState();
  const target = slots.find(s => s.id === slotId);
  if (!target || target.splitPart) return;

  const { parts, blocksPerPart } = config;
  const splitGroupId = `split_${slotId}_${Date.now()}`;
  const partTimes = computePartTimes(target.startTime, blocksPerPart);

  const newParts: ProgrammingRow[] = blocksPerPart.map((blocks, index) => {
    const partStart = partTimes[index].startTime;
    const partEnd = partTimes[index].endTime;
    const partBlocks = filterBlocksInRange(target.blocks, partStart, partEnd);
    return {
      ...target,
      id: `${slotId}_part${index + 1}`,
      splitPart: `${index + 1}/${parts}`,
      splitGroupId,
      durationBlocks: blocks,
      durationHours: blocksToHours(blocks),
      startTime: partStart,
      endTime: partEnd,
      blocks: partBlocks,
      blocksIds: partBlocks.map(b => b.id),
      instructors: target.instructors.map(instructor => ({ ...instructor })),
      classrooms: target.classrooms.map(classroom => ({ ...classroom })),
      isSelected: false,
      ...(index > 0 && { stageId: null }),
    };
  });

  const updated = slots.flatMap(s => (s.id === slotId ? newParts : [s]));
  useProgrammingEditStore.setState({
    slots: updated,
    hasChanges: computeHasChanges(updated, originalSlots),
  });
  computeSummaryFromSlots();
};

/**
 * Merges split parts back into a single slot.
 * Uses splitGroupId to identify the rows to reunify.
 */
export const reunifySlot = (splitGroupId: string) => {
  const { slots, originalSlots } = useProgrammingEditStore.getState();
  const parts = slots.filter(s => s.splitGroupId === splitGroupId);
  if (parts.length < 2) return;

  const base = parts[0];
  const lastPart = parts[parts.length - 1];
  const totalBlocks = parts.reduce((sum, p) => sum + p.durationBlocks, 0);

  const unified: ProgrammingRow = {
    ...base,
    id: base.id.replace(/_part\d+$/, ""),
    splitPart: null,
    splitGroupId: null,
    startTime: base.startTime,
    endTime: lastPart.endTime,
    durationBlocks: totalBlocks,
    durationHours: blocksToHours(totalBlocks),
    blocks: parts.flatMap(p => p.blocks),
    blocksIds: parts.flatMap(p => p.blocksIds),
    isSelected: false,
  };

  const updated = slots.flatMap(s => {
    if (s.splitGroupId === splitGroupId) {
      return s === parts[0] ? [unified] : [];
    }
    return [s];
  });

  useProgrammingEditStore.setState({
    slots: updated,
    hasChanges: computeHasChanges(updated, originalSlots),
  });
  computeSummaryFromSlots();
};

/** Toggles the checkbox selection of a single slot. Does not affect hasChanges. */
export const toggleSlotSelection = (slotId: string) => {
  const { slots } = useProgrammingEditStore.getState();
  useProgrammingEditStore.setState({
    slots: slots.map(s => (s.id === slotId ? { ...s, isSelected: !s.isSelected } : s)),
  });
};

/** Toggles all slot checkboxes. Does not affect hasChanges. */
export const toggleAllSlots = (checked: boolean) => {
  const { slots } = useProgrammingEditStore.getState();
  useProgrammingEditStore.setState({
    slots: slots.map(s => ({ ...s, isSelected: checked })),
  });
};

/**
 * Assigns a stage to all selected slots.
 * Recalculates hasChanges after updating.
 */
export const bulkUpdateStage = (stageId: string | null) => {
  const { slots, originalSlots } = useProgrammingEditStore.getState();
  const updated = slots.map(s => (s.isSelected ? { ...s, stageId } : s));
  useProgrammingEditStore.setState({
    slots: updated,
    hasChanges: computeHasChanges(updated, originalSlots),
  });
  computeSummaryFromSlots();
};

/**
 * Assigns instructors to all selected slots.
 * Recalculates hasChanges after updating.
 */
export const bulkUpdateInstructors = (instructors: SlotInstructor[]) => {
  const { slots, originalSlots } = useProgrammingEditStore.getState();
  const updated = slots.map(s => (s.isSelected ? { ...s, instructors } : s));
  useProgrammingEditStore.setState({
    slots: updated,
    hasChanges: computeHasChanges(updated, originalSlots),
  });
};

/**
 * Assigns classrooms to all selected slots.
 * Recalculates hasChanges after updating.
 */
export const bulkUpdateClassrooms = (classrooms: SlotClassroom[]) => {
  const { slots, originalSlots } = useProgrammingEditStore.getState();
  const updated = slots.map(s => (s.isSelected ? { ...s, classrooms } : s));
  useProgrammingEditStore.setState({
    slots: updated,
    hasChanges: computeHasChanges(updated, originalSlots),
  });
};

/** Applies partial filter updates (client-side, does not re-fetch). */
export const setProgrammingFilters = (filters: Partial<ProgrammingFilters>) => {
  const current = useProgrammingEditStore.getState().filters;
  useProgrammingEditStore.setState({ filters: { ...current, ...filters } });
};

/** Resets all table filters. */
export const resetProgrammingFilters = () =>
  useProgrammingEditStore.setState({ filters: structuredClone(initialFilters) });

/** Sets the loading flag while fetching data from the API. */
export const setProgrammingLoading = (isLoading: boolean) =>
  useProgrammingEditStore.setState({ isLoading });

/** Sets the validating flag while the "Validate and apply" mutation is running. */
export const setProgrammingValidating = (isValidating: boolean) =>
  useProgrammingEditStore.setState({ isValidating });

/**
 * Stores preview sections + changeset and activates the programming results view.
 * Called after a successful dryRun from ProgrammingEdit.
 */
export const setProgrammingResults = (
  previewSections: MicroprogrammingPreviewSection[],
  programmingChangeset: ProgrammingSessionCrudInput,
) =>
  useProgrammingEditStore.setState({
    previewSections,
    programmingChangeset,
    isProgrammingResultsActive: true,
  });

/**
 * Clears programming results and deactivates the results view.
 * Called on "Volver" from ResultEdit or after a successful commit.
 */
export const clearProgrammingResults = () =>
  useProgrammingEditStore.setState({
    previewSections: null,
    programmingChangeset: null,
    isProgrammingResultsActive: false,
  });
